import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { notifyHost } from '../../../lib/notificationDispatch';
import Icon from '../../../components/AppIcon';
import ToastNotification from './ToastNotification';

const StarRating = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5]?.map((star) => (
      <Icon
        key={star}
        name="Star"
        size={12}
        className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
      />
    ))}
    <span className="ml-1 text-xs text-muted-foreground">{rating}/5</span>
  </div>
);

const STATUS_BADGE = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  denied: 'bg-red-100 text-red-800 border-red-200',
};

const ReviewRemovalsTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal state
  const [confirmModal, setConfirmModal] = useState(null); // { type: 'remove'|'deny', request }
  const [adminNote, setAdminNote] = useState('');
  const [modalError, setModalError] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        ?.from('review_removal_requests')
        ?.select(`
          id, host_reason, status, admin_note, reviewed_at, created_at,
          review_id,
          event_id,
          host_id,
          event_reviews (
            id, rating, review_text, parent_name, parent_email, is_visible, event_title
          ),
          events (
            id, title
          ),
          event_host_profiles (
            id, full_name, organization_name, email
          )
        `)
        ?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setRequests(data || []);
    } catch (err) {
      console.error('fetchRequests error:', err);
      setError(err?.message || 'Failed to load review removal requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3500);
  };

  const openRemoveModal = (request) => {
    setConfirmModal({ type: 'remove', request });
    setAdminNote('');
    setModalError(null);
  };

  const openDenyModal = (request) => {
    setConfirmModal({ type: 'deny', request });
    setAdminNote('');
    setModalError(null);
  };

  const closeModal = () => {
    setConfirmModal(null);
    setAdminNote('');
    setModalError(null);
  };

  const handleRemoveReview = async () => {
    if (!confirmModal?.request) return;
    setActionLoading(true);
    setModalError(null);
    try {
      const req = confirmModal?.request;

      // Hide the review
      const { error: reviewError } = await supabase
        ?.from('event_reviews')
        ?.update({ is_visible: false })
        ?.eq('id', req?.review_id);
      if (reviewError) throw reviewError;

      // Mark request as approved
      const { error: reqError } = await supabase
        ?.from('review_removal_requests')
        ?.update({
          status: 'approved',
          admin_note: adminNote?.trim() || null,
          reviewed_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', req?.id);
      if (reqError) throw reqError;

      const eventTitle = req?.events?.title || 'your event';
      const host = req?.event_host_profiles;
      await notifyHost({
        userId: req?.host_id,
        eventId: req?.event_id,
        notificationType: 'review',
        title: 'Removal Request Approved',
        body: `Your request to remove a review on "${eventTitle}" was approved — the review is now hidden.`,
        emailTemplate: 'admin_notification',
        emailTo: host?.email,
        emailSubject: 'Your Review Removal Request Was Approved',
        emailData: {
          user_name: host?.full_name || 'there',
          heading: 'Removal Request Approved',
          emoji: '✅',
          intro: 'The KidQuest team reviewed your request and approved it — the review has been hidden.',
          detail_label: 'Event',
          detail_value: eventTitle,
        },
      });

      showSuccess('Review has been removed successfully.');
      closeModal();
      fetchRequests();
    } catch (err) {
      setModalError(err?.message || 'Failed to remove review');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDenyRequest = async () => {
    if (!confirmModal?.request) return;
    setActionLoading(true);
    setModalError(null);
    try {
      const req = confirmModal?.request;

      const { error: reqError } = await supabase
        ?.from('review_removal_requests')
        ?.update({
          status: 'denied',
          admin_note: adminNote?.trim() || null,
          reviewed_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', req?.id);
      if (reqError) throw reqError;

      const eventTitle = req?.events?.title || 'your event';
      const host = req?.event_host_profiles;
      const note = adminNote?.trim();
      await notifyHost({
        userId: req?.host_id,
        eventId: req?.event_id,
        notificationType: 'review',
        title: 'Removal Request Denied',
        body: note
          ? `Your request to remove a review on "${eventTitle}" was denied. Reason: ${note}`
          : `Your request to remove a review on "${eventTitle}" was denied by the admin.`,
        emailTemplate: 'admin_notification',
        emailTo: host?.email,
        emailSubject: 'Update on Your Review Removal Request',
        emailData: {
          user_name: host?.full_name || 'there',
          heading: 'Removal Request Denied',
          emoji: '🚫',
          intro: 'The KidQuest team reviewed your request to remove a review and denied it.',
          detail_label: 'Event',
          detail_value: eventTitle,
          detail_label_2: note ? 'Reason' : '',
          detail_value_2: note || '',
          tone: 'negative',
        },
      });

      showSuccess('Request has been denied. The review remains visible.');
      closeModal();
      fetchRequests();
    } catch (err) {
      setModalError(err?.message || 'Failed to deny request');
    } finally {
      setActionLoading(false);
    }
  };

  const pendingCount = requests?.filter((r) => r?.status === 'pending')?.length;

  return (
    <div>
      {/* Popup Toast Notifications */}
      <ToastNotification
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Loading removal requests...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Icon name="AlertCircle" size={32} className="text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
          <button
            onClick={fetchRequests}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : requests?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Icon name="MessageSquare" size={32} className="text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No review removal requests found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests?.map((req) => {
            const review = req?.event_reviews;
            const host = req?.event_host_profiles;
            const eventTitle = req?.events?.title || review?.event_title || '—';
            const isPending = req?.status === 'pending';

            return (
              <div
                key={req?.id}
                className="bg-card border border-border rounded-xl p-5 space-y-4"
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-foreground text-sm truncate">
                        {eventTitle}
                      </h3>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_BADGE?.[req?.status]}`}
                      >
                        {req?.status === 'pending' ?'Pending'
                          : req?.status === 'approved' ?'Approved' :'Denied'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Requested by{' '}
                      <span className="font-medium text-foreground">
                        {host?.organization_name || host?.full_name || '—'}
                      </span>
                      {host?.email && (
                        <span className="ml-1 text-muted-foreground">({host?.email})</span>
                      )}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                    {req?.created_at
                      ? new Date(req?.created_at)?.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '—'}
                  </p>
                </div>
                {/* Flagged Review */}
                {review && (
                  <div className="bg-muted/40 border border-border rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Flagged Review
                      </p>
                      <StarRating rating={review?.rating} />
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">
                      {review?.review_text || <span className="italic text-muted-foreground">No review text</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      By {review?.parent_name || review?.parent_email || 'Anonymous'}
                      {!review?.is_visible && (
                        <span className="ml-2 inline-flex items-center gap-1 text-orange-600">
                          <Icon name="EyeOff" size={10} /> Hidden
                        </span>
                      )}
                    </p>
                  </div>
                )}
                {/* Host's reason */}
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Host's Reason for Removal
                  </p>
                  <p className="text-sm text-foreground bg-orange-50 border border-orange-100 rounded-lg px-3 py-2 leading-relaxed">
                    {req?.host_reason && req?.host_reason?.trim() !== ''
                      ? req?.host_reason
                      : <span className="italic text-muted-foreground">No reason provided</span>}
                  </p>
                </div>
                {/* Admin note (if reviewed) */}
                {req?.admin_note && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Admin Note
                    </p>
                    <p className="text-sm text-foreground bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 leading-relaxed">
                      {req?.admin_note}
                    </p>
                  </div>
                )}
                {/* Actions */}
                {isPending && (
                  <div className="flex items-center gap-3 pt-1">
                    <button
                      onClick={() => openRemoveModal(req)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Icon name="Trash2" size={14} />
                      Remove Review
                    </button>
                    <button
                      onClick={() => openDenyModal(req)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                    >
                      <Icon name="XCircle" size={14} />
                      Deny
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white border border-border rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">
                {confirmModal?.type === 'remove' ? 'Remove Review' : 'Deny Request'}
              </h3>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                {confirmModal?.type === 'remove' ?'This will hide the review from the event immediately. The host will see the request as "Approved".' : 'The review will remain visible. The host will see the request as"Denied".'}
              </p>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Admin Note <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e?.target?.value)}
                  placeholder={
                    confirmModal?.type === 'remove' ?'Add a note about why this review was removed...' :'Add a note explaining why this request was denied...'
                  }
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                />
              </div>

              {modalError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  <Icon name="AlertCircle" size={14} />
                  {modalError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-border rounded-xl hover:bg-muted transition-colors text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={
                    confirmModal?.type === 'remove' ? handleRemoveReview : handleDenyRequest
                  }
                  disabled={actionLoading}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 ${
                    confirmModal?.type === 'remove' ?'bg-red-600 text-white hover:bg-red-700' :'bg-foreground text-background hover:bg-foreground/90'
                  }`}
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : confirmModal?.type === 'remove' ? (
                    <>
                      <Icon name="Trash2" size={14} />
                      Confirm Removal
                    </>
                  ) : (
                    <>
                      <Icon name="XCircle" size={14} />
                      Confirm Deny
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewRemovalsTab;
